import React, { useState } from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TreeNode {
  name: string;
  children: Map<string, TreeNode>;
  isFile: boolean;
}

// ---------------------------------------------------------------------------
// Utility: buildTree
// ---------------------------------------------------------------------------

/**
 * Converts a flat array of S3 object keys into a hierarchical TreeNode tree.
 * - Strips leading slashes from each key.
 * - Deduplicates keys using a Set before processing (Req 2.7).
 * - Intermediate path segments become folder nodes; terminal segments become file leaf nodes.
 */
export function buildTree(keys: string[]): TreeNode {
  const root: TreeNode = { name: '', children: new Map(), isFile: false };
  const seen = new Set<string>();

  for (const key of keys) {
    const normalized = key.replace(/^\//, '');
    if (seen.has(normalized)) continue;
    seen.add(normalized);

    const parts = normalized.split('/').filter(Boolean);
    let node = root;
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      if (!node.children.has(part)) {
        node.children.set(part, {
          name: part,
          children: new Map(),
          isFile: i === parts.length - 1,
        });
      }
      node = node.children.get(part)!;
    }
  }
  return root;
}

// ---------------------------------------------------------------------------
// Utility: isExpired
// ---------------------------------------------------------------------------

/**
 * Returns true if the given urlExpiresAt value is null, undefined, non-parseable,
 * or represents a timestamp at or before the current time. Returns false only when
 * the parsed timestamp is strictly in the future.
 */
export function isExpired(urlExpiresAt: string | null | undefined): boolean {
  if (!urlExpiresAt) return true;
  const t = new Date(urlExpiresAt).getTime();
  if (isNaN(t)) return true;
  return Date.now() >= t;
}

// ---------------------------------------------------------------------------
// Utility: formatCountdown
// ---------------------------------------------------------------------------

/**
 * Returns a human-readable countdown string derived from urlExpiresAt:
 * - null / undefined / non-parseable / expired → "Expired"
 * - remaining > 3 600 000 ms → "{N}h {M}m"
 * - remaining 1–3 600 000 ms → "{M}m {S}s"
 */
export function formatCountdown(urlExpiresAt: string | null | undefined): string {
  if (!urlExpiresAt) return 'Expired';
  const t = new Date(urlExpiresAt).getTime();
  if (isNaN(t)) return 'Expired';
  const remaining = t - Date.now();
  if (remaining <= 0) return 'Expired';
  if (remaining > 3_600_000) {
    const h = Math.floor(remaining / 3_600_000);
    const m = Math.floor((remaining % 3_600_000) / 60_000);
    return `${h}h ${m}m`;
  }
  const m = Math.floor(remaining / 60_000);
  const s = Math.floor((remaining % 60_000) / 1_000);
  return `${m}m ${s}s`;
}

// ---------------------------------------------------------------------------
// Internal sub-component: FileTreeNode
// ---------------------------------------------------------------------------

interface FileTreeNodeProps {
  node: TreeNode;
  depth: number;
}

const FileTreeNode: React.FC<FileTreeNodeProps> = ({ node, depth }) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(true);

  const indentStyle: React.CSSProperties = { paddingLeft: `${depth * 1.25}rem` };

  if (node.isFile) {
    return (
      <div
        className="flex items-center gap-1 py-0.5 text-gray-700"
        style={indentStyle}
      >
        {/* Document icon */}
        <svg
          className="w-4 h-4 shrink-0 text-gray-400"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
            clipRule="evenodd"
          />
        </svg>
        <span className="truncate">{node.name}</span>
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        className="flex items-center gap-1 w-full text-left py-0.5 hover:bg-gray-100 rounded text-gray-800"
        style={indentStyle}
        onClick={() => setIsExpanded((prev) => !prev)}
        aria-expanded={isExpanded}
      >
        {/* Chevron icon */}
        <svg
          className={`w-3 h-3 shrink-0 text-gray-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
            clipRule="evenodd"
          />
        </svg>
        {/* Folder icon */}
        <svg
          className="w-4 h-4 shrink-0 text-yellow-500"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
        </svg>
        <span className="truncate font-medium">{node.name}</span>
      </button>
      {isExpanded && (
        <div>
          {Array.from(node.children.values()).map((child) => (
            <FileTreeNode key={child.name} node={child} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Public component: FileTree (default export)
// ---------------------------------------------------------------------------

interface FileTreeProps {
  files: string[];
}

const FileTree: React.FC<FileTreeProps> = ({ files }) => {
  if (files.length === 0) {
    return (
      <p className="text-sm text-gray-500 italic py-2">
        No files recorded for this archive.
      </p>
    );
  }

  const tree = buildTree(files);

  return (
    <div className="text-sm font-mono">
      {Array.from(tree.children.values()).map((node) => (
        <FileTreeNode key={node.name} node={node} depth={0} />
      ))}
    </div>
  );
};

export default FileTree;
